/**
 * ComplianceEnableOverrideDialog
 *
 * Shared dialog used in two places:
 *  1. Compliance Center → Tenant Compliance Overrides panel (compliance-page.tsx)
 *  2. Organizations → Tenant card → Compliance Settings section (admin-dashboard.tsx)
 *
 * Removes the platform-administrator override for one framework on one tenant,
 * restoring access according to their plan. Org admins are notified by email.
 */
import { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export interface ComplianceEnableOverrideDialogProps {
  frameworkKey: string;
  frameworkName: string;
  orgId: string;
  orgName: string;
  onClose: () => void;
  /** Called after a successful enable so the parent can refresh its data. */
  onEnabled: () => void;
}

export function ComplianceEnableOverrideDialog({
  frameworkKey,
  frameworkName,
  orgId,
  orgName,
  onClose,
  onEnabled,
}: ComplianceEnableOverrideDialogProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  async function confirm() {
    if (!orgId) {
      toast({ title: "Cannot determine org — please reload", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      // Fetch the current override list, strip this framework, then PATCH back.
      const overridesRes = await apiRequest(
        "GET",
        `/api/admin/organizations/${orgId}/compliance-overrides`,
      );
      const overridesData = await overridesRes.json();
      const remaining: string[] = (overridesData.frameworks ?? [])
        .filter((f: any) => f.disabledByAdmin)
        .map((f: any) => f.key as string)
        .filter((k: string) => k !== frameworkKey);

      await apiRequest("PATCH", `/api/admin/organizations/${orgId}/compliance-overrides`, {
        disabledFrameworks: remaining,
      });

      // Invalidate both the per-org cache (tenant card) and the summary cache
      // (Compliance Center panel) so both surfaces refresh immediately.
      queryClient.invalidateQueries({
        queryKey: [`/api/admin/organizations/${orgId}/compliance-overrides`],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/compliance-overrides/summary"],
      });

      toast({
        title: "Framework enabled",
        description: `${frameworkName} override removed for ${orgName}. Access returns to plan rules. Org admins notified by email.`,
      });
      onEnabled();
    } catch (e: any) {
      toast({ title: "Failed to remove override", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Enable Compliance Framework</DialogTitle>
          <DialogDescription>
            This removes the platform administrator override and restores access based on plan rules.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1 text-sm">
          <p>You are enabling this framework for:</p>
          <div className="rounded-md border px-4 py-3 space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Tenant</p>
            <p className="font-semibold">{orgName}</p>
          </div>
          <div className="rounded-md border px-4 py-3 space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Framework</p>
            <p className="font-semibold">{frameworkName}</p>
          </div>
          <div className="rounded-md bg-sky-500/8 border border-sky-500/20 px-3 py-2 text-xs text-sky-700 dark:text-sky-300">
            After confirmation the framework will be available according to the tenant's subscription
            plan. Org admins will receive an email notification.
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={confirm}
            disabled={saving}
            data-testid="button-confirm-enable-framework"
          >
            {saving ? "Enabling…" : "Enable Framework"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
